import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import * as XLSX from 'xlsx';
import {
  fetchAllProducts,
  deleteProduct,
  bulkImportProducts,
} from '../../services/productServices';
import { useSettings } from '../../context/SettingsContext';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatDate } from '../../utils/dateFormatter';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import ConfirmDialog from '../ui/ConfirmDialog';
import NotificationSnackbar from '../ui/NotificationSnackbar';
import { CrmBadge, CrmButton, CrmTooltip } from '../ui';
import { CrmEntityListPage } from '../listing';


const exportProducts = (rows, filename) => {
  const worksheet = XLSX.utils.json_to_sheet(rows.map((product) => ({
    ID: product.id,
    Name: product.name || '',
    Brand: product.brand || '',
    Category: product.category_name || '',
    Type: product.type || '',
    SKU: product.sku || '',
    Cost: product.cost || 0,
    'Selling price': product.selling_price || 0,
    'Sale price': product.sale_price || '',
    Status: Number(product.is_active || 0) === 1 ? 'Active' : 'Inactive',
  })));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');
  XLSX.writeFile(workbook, filename);
};

function ProductList() {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [bulkDeleteIds, setBulkDeleteIds] = useState([]);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const loadProducts = async ({ isAutoRefresh = false } = {}) => {
    if (!isAutoRefresh) setLoading(true);
    try {
      const response = await fetchAllProducts();
      setProducts(Array.isArray(response) ? response : []);
    } catch {
      setNotification({ open: true, message: 'Failed to load products.', severity: 'error' });
    } finally {
      if (!isAutoRefresh) setLoading(false);
    }
  };

  useAutoRefresh(loadProducts, { intervalMs: 20000 });

  const openProduct = (product) => navigate(`/products/${product.id}/edit`);

  const duplicateProduct = (product) => navigate(`/products/new?duplicate=${product.id}`);

  const handleBulkFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    try {
      const result = await bulkImportProducts(file);
      await loadProducts({ isAutoRefresh: true });
      setNotification({
        open: true,
        severity: result.failed > 0 ? 'warning' : 'success',
        message: result.failed > 0
          ? `Imported ${result.success}/${result.total} products. ${result.failed} failed.`
          : `Successfully imported ${result.success} products`,
      });
    } catch {
      setNotification({ open: true, severity: 'error', message: 'Bulk import failed' });
    }
  };

  const confirmDelete = async () => {
    try {
      const ids = productToDelete === 'BULK' ? bulkDeleteIds : [productToDelete];
      await Promise.all(ids.filter(Boolean).map((id) => deleteProduct(id)));
      await loadProducts({ isAutoRefresh: true });
      setNotification({ open: true, message: `${ids.length} product${ids.length === 1 ? '' : 's'} deleted`, severity: 'success' });
    } catch {
      setNotification({ open: true, message: 'Failed to delete product(s).', severity: 'error' });
    } finally {
      setConfirmOpen(false);
      setProductToDelete(null);
      setBulkDeleteIds([]);
    }
  };


  const categoryOptions = useMemo(() => [...new Set(products.map((product) => product.category_name).filter(Boolean))]
    .sort().map((value) => ({ value, label: value })), [products]);
  const typeOptions = useMemo(() => [...new Set(products.map((product) => product.type).filter(Boolean))]
    .sort().map((value) => ({ value, label: value })), [products]);

  const columns = useMemo(() => [
    {
      id: 'name', label: 'Name', width: 250, minWidth: 200, maxWidth: 440,
      hideable: false, pinnable: true, stopRowClick: true,
      renderCell: (product) => <button type="button" className="crm-table-primary-link" onClick={() => openProduct(product)}>{product.name || '—'}</button>,
    },
    { id: 'brand', label: 'Brand', width: 170, minWidth: 130, maxWidth: 280 },
    { id: 'category_name', label: 'Category', width: 190, minWidth: 150, maxWidth: 320, filterable: true },
    { id: 'type', label: 'Type', width: 150, minWidth: 120, maxWidth: 240, filterable: true },
    {
      id: 'is_active', label: 'Status', width: 130, minWidth: 110, maxWidth: 180, type: 'boolean', filterable: true,
      accessor: (product) => Boolean(Number(product.is_active)),
      renderCell: (product) => <CrmBadge tone={Number(product.is_active) === 1 ? 'success' : 'neutral'}>{Number(product.is_active) === 1 ? 'Active' : 'Inactive'}</CrmBadge>,
    },
    {
      id: 'selling_price', label: 'Price', width: 190, minWidth: 150, maxWidth: 300, type: 'currency',
      accessor: (product) => Number(product.sale_price || product.selling_price || 0),
      renderCell: (product) => (
        <span>
          <strong>{formatCurrency(product.sale_price || product.selling_price, settings?.currency_code)}</strong>
          {product.sale_price ? <small className="crm-price-strike">{formatCurrency(product.selling_price, settings?.currency_code)}</small> : null}
          {product.selling_price_unit ? <small> / {product.selling_price_unit}</small> : null}
        </span>
      ),
    },
    { id: 'created_at', label: 'Created', width: 160, minWidth: 140, maxWidth: 240, type: 'date', defaultHidden: true, renderCell: (product) => formatDate(product.created_at) || '—' },
    {
      id: 'row_actions', label: 'Actions', width: 132, minWidth: 132, maxWidth: 156,
      hideable: false, sortable: false, searchable: false, className: 'crm-data-table__actions-cell',
      renderCell: (product) => (
        <span className="crm-row-actions">
          <CrmTooltip content="Edit product"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => openProduct(product)} aria-label="Edit product"><EditOutlinedIcon /></CrmButton></CrmTooltip>
          <CrmTooltip content="Duplicate product"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => duplicateProduct(product)} aria-label="Duplicate product"><ContentCopyOutlinedIcon /></CrmButton></CrmTooltip>
          <CrmTooltip content="Delete product"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action crm-row-action--danger" onClick={() => { setProductToDelete(product.id); setConfirmOpen(true); }} aria-label="Delete product"><DeleteOutlineIcon /></CrmButton></CrmTooltip>
        </span>
      ),
    },
  ], [products, settings?.currency_code]);

  const filters = useMemo(() => [
    { id: 'created', columnId: 'created_at', label: 'Created date', type: 'date-range', quick: true },
    { id: 'category', columnId: 'category_name', label: 'Category', type: 'select', options: categoryOptions, quick: true },
    { id: 'type', columnId: 'type', label: 'Type', type: 'select', options: typeOptions, quick: true },
    { id: 'active', columnId: 'is_active', label: 'Status', type: 'select', options: [{ value: true, label: 'Active' }, { value: false, label: 'Inactive' }], quick: true },
  ], [categoryOptions, typeOptions]);

  return (
    <div className="contacts-page">
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleBulkFileChange} />
      <CrmEntityListPage
        moduleKey="products"
        title="Products"
        itemLabel="product"
        items={products}
        columns={columns}
        filterDefinitions={filters}
        initialSort={{ columnId: 'created_at', direction: 'desc' }}
        loading={loading}
        onRefresh={() => loadProducts()}
        onCreate={() => navigate('/products/new')}
        createLabel="Add product"
        onImport={() => fileInputRef.current?.click()}
        onRowClick={openProduct}
        onExport={(rows) => exportProducts(rows, 'products.xlsx')}
        onExportSelected={(rows) => exportProducts(rows, 'selected_products.xlsx')}
        onDeleteSelected={(ids) => { setBulkDeleteIds(ids); setProductToDelete('BULK'); setConfirmOpen(true); }}
      />
      <ConfirmDialog open={confirmOpen} title="Delete product?" message={productToDelete === 'BULK' ? `Delete ${bulkDeleteIds.length} selected products? This cannot be undone.` : 'This product will be permanently deleted.'} confirmText="Delete" onCancel={() => setConfirmOpen(false)} onConfirm={confirmDelete} />
      <NotificationSnackbar {...notification} onClose={() => setNotification((previous) => ({ ...previous, open: false }))} />
    </div>
  );
}

export default ProductList;
